import { XMarkIcon } from "@heroicons/react/24/outline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FC, useState } from "react";

import { Button, Headline, Small } from "@fronterahealth/frontera-ui-components";

import { customFetch } from "@api/rest/custom-fetcher";
import { BaseQAPackageCard } from "@pages/QASettings/BaseQAPackageCard";
import { BaseQAPackageForm, type CreatePackageFormData } from "@pages/QASettings/BaseQAPackageForm";
import { type ApiListResponse, type ApiResponse, type QAPackage, type QARule } from "@pages/QASettings/baseQaTypes";
import { serializeRuleBody } from "@pages/QASettings/qaRuleBlob";

// ---------------------------------------------------------------------------
// Import modal
// ---------------------------------------------------------------------------

interface ImportModalProps {
  onClose: () => void;
  onImport: (packages: (Omit<QAPackage, "id" | "rules"> & { rules: Omit<QARule, "id">[] })[]) => void;
}

const ImportModal: FC<ImportModalProps> = ({ onClose, onImport }) => {
  const [json, setJson] = useState("");
  const [error, setError] = useState("");

  const handleImport = () => {
    setError("");
    try {
      const parsed = JSON.parse(json);
      const arr = Array.isArray(parsed) ? parsed : [parsed];
      onImport(arr);
      onClose();
    } catch {
      setError("Invalid JSON — paste a single package object or an array of packages.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg rounded-lg bg-bg-surface p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <Headline size="small">Import packages</Headline>
          <button onClick={onClose} className="text-text-tertiary hover:text-text-primary">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <p className="mb-3 text-sm text-text-secondary">
          Paste a single package JSON object or an array of packages exported from this tool.
        </p>
        <textarea
          className="w-full rounded border border-border-medium bg-bg-default px-3 py-2 font-mono text-xs text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-globemallow-600"
          placeholder='{"name": "...", "rules": [...], ...}'
          rows={12}
          value={json}
          onChange={(e) => setJson(e.target.value)}
        />
        {error && <p className="mt-1 text-xs text-text-error-primary">{error}</p>}
        <div className="mt-4 flex gap-2">
          <Button appearance="primary" text="Import" onClick={handleImport} />
          <Button appearance="secondary" text="Cancel" onClick={onClose} />
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const BaseQAManager: FC = () => {
  const queryClient = useQueryClient();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  // ---- Queries ----

  const { data: packagesData, isLoading: packagesLoading } = useQuery({
    queryKey: ["qa-packages"],
    queryFn: () => customFetch<ApiResponse<ApiListResponse<QAPackage>>>(`/api/basecamp/qa-packages`),
  });

  const { data: payorTypesData } = useQuery({
    queryKey: ["payor-types"],
    queryFn: () => customFetch<ApiResponse<{ results: string[] }>>(`/api/basecamp/payor-types`),
  });

  const packages = packagesData?.status === 200 ? (packagesData.data.results ?? []) : [];
  const payorTypes = payorTypesData?.status === 200 ? (payorTypesData.data.results ?? []) : [];

  const invalidatePackages = () => queryClient.invalidateQueries({ queryKey: ["qa-packages"] });

  // ---- Mutations ----

  const { mutateAsync: createPackage, isPending: isCreating } = useMutation({
    mutationFn: (body: {
      name: string;
      description: string;
      instructions: string;
      package_type: QAPackage["package_type"];
      payor_types?: string[];
      report_types?: string[];
    }) =>
      customFetch(`/api/basecamp/qa-packages/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => void invalidatePackages(),
  });

  const { mutateAsync: createRule } = useMutation({
    mutationFn: (body: { package_id: number; rule: string; instructions?: string }) =>
      customFetch(`/api/basecamp/qa-rules/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => void invalidatePackages(),
  });

  const { mutateAsync: updatePackage } = useMutation({
    mutationFn: (body: {
      id: number;
      name?: string;
      description?: string;
      instructions?: string;
      payor_types?: string[];
      report_types?: string[];
    }) =>
      customFetch(`/api/basecamp/qa-packages/update`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => void invalidatePackages(),
  });

  const { mutateAsync: duplicatePackage } = useMutation({
    mutationFn: (id: number) =>
      customFetch(`/api/basecamp/qa-packages/duplicate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      }),
    onSuccess: () => void invalidatePackages(),
  });

  const { mutateAsync: deletePackage } = useMutation({
    mutationFn: (id: number) =>
      customFetch(`/api/basecamp/qa-packages/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      }),
    onSuccess: () => void invalidatePackages(),
  });

  const { mutateAsync: toggleGlobal } = useMutation({
    mutationFn: ({ id, isGloballyDisabled }: { id: number; isGloballyDisabled: boolean }) =>
      customFetch(`/api/basecamp/qa-packages/update`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, is_globally_disabled: isGloballyDisabled }),
      }),
    onSuccess: () => void invalidatePackages(),
  });

  const { mutateAsync: updateRule } = useMutation({
    mutationFn: (body: { id: number; rule?: string; instructions?: string }) =>
      customFetch(`/api/basecamp/qa-rules/update`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => void invalidatePackages(),
  });

  const { mutateAsync: deleteRule } = useMutation({
    mutationFn: (id: number) =>
      customFetch(`/api/basecamp/qa-rules/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      }),
    onSuccess: () => void invalidatePackages(),
  });

  // ---- Handlers ----

  const handleCreatePackage = async (formData: CreatePackageFormData) => {
    const result = await createPackage({
      name: formData.name,
      description: formData.description,
      instructions: formData.instructions,
      package_type: formData.package_type,
      payor_types: formData.payor_types.length > 0 ? formData.payor_types : undefined,
      report_types: formData.report_types,
    });

    // Add rules after package is created — draft rules are held as
    // structured field objects (see BaseQAPackageForm/QARuleEditor) and
    // serialized into the `instructions` blob here, same as
    // handleAddBlankRule below.
    const created = result as ApiResponse<QAPackage>;
    if (created?.data?.id && formData.rules.length > 0) {
      for (const r of formData.rules) {
        await createRule({
          package_id: created.data.id,
          rule: r.rule,
          instructions: serializeRuleBody({
            types: r.types,
            location: r.location,
            scanFor: r.scanFor,
            feedback: r.feedback,
          }),
        });
      }
    }

    setShowCreateForm(false);
  };

  const handleImport = async (imported: (Omit<QAPackage, "id" | "rules"> & { rules: Omit<QARule, "id">[] })[]) => {
    for (const pkg of imported) {
      const result = await createPackage({
        name: pkg.name,
        description: pkg.description,
        instructions: pkg.instructions,
        package_type: pkg.package_type,
        payor_types: pkg.payor_types,
        report_types: pkg.report_types,
      });
      const created = result as ApiResponse<QAPackage>;
      if (created?.data?.id && pkg.rules?.length > 0) {
        for (const r of pkg.rules) {
          await createRule({
            package_id: created.data.id,
            rule: r.rule,
            instructions: r.instructions || undefined,
          });
        }
      }
    }
  };

  // A freshly-added rule is created blank and immediately opens in the
  // card's structured editor (see BaseQAPackageCard's auto-reveal effect).
  const handleAddBlankRule = async (packageId: number) => {
    await createRule({
      package_id: packageId,
      rule: "",
      instructions: serializeRuleBody({ types: ["IA", "RA"], location: "", scanFor: "", feedback: "" }),
    });
  };

  // ---- Render ----

  return (
    <div className="mx-auto flex w-full flex-col content-between items-center lg:flex-col lg:items-start px-16 py-8">
      {/* Internal banner */}
      {/* No Mosaic warning-banner token; raw amber palette used intentionally for this preserved internal banner. */}
      <div className="mb-6 w-full rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center rounded-full bg-amber-200 px-2 py-0.5 text-xs font-semibold text-amber-800">
            Frontera Internal
          </span>
          <span className="text-sm text-amber-800">Changes here affect all organizations.</span>
        </div>
      </div>

      {/* Page header + actions */}
      <div className="flex w-full items-center justify-between">
        <div>
          <Headline size="medium">Base QA manager</Headline>
          <p className="mt-1 text-sm text-text-secondary">
            Authoring view — manages what&apos;s available to all orgs.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button appearance="secondary" text="Import" onClick={() => setShowImportModal(true)} />
          <Button
            appearance="primary"
            text={showCreateForm ? "Cancel" : "+ Add package"}
            onClick={() => setShowCreateForm((p) => !p)}
          />
        </div>
      </div>

      {/* Create form */}
      {showCreateForm && (
        <div className="w-full">
          <BaseQAPackageForm
            payorTypes={payorTypes}
            onCancel={() => setShowCreateForm(false)}
            onSubmit={handleCreatePackage}
            isPending={isCreating}
          />
        </div>
      )}

      {/* Package list */}
      {packagesLoading ? (
        <div className="mt-8 flex w-full items-center justify-center rounded-lg border border-dashed border-border-subtle p-12">
          <Small colorType="secondary">Loading…</Small>
        </div>
      ) : packages.length === 0 ? (
        <div className="mt-8 flex w-full items-center justify-center rounded-lg border border-dashed border-border-subtle p-12">
          <Small colorType="secondary">No packages yet. Use &quot;+ Add package&quot; to create one.</Small>
        </div>
      ) : (
        <div className="mt-6 flex w-full flex-col gap-3">
          {packages.map((pkg) => (
            <BaseQAPackageCard
              key={pkg.id}
              pkg={pkg}
              payorTypes={payorTypes}
              onUpdate={updatePackage}
              onDuplicate={duplicatePackage}
              onDelete={deletePackage}
              onToggleGlobal={toggleGlobal}
              onAddRule={handleAddBlankRule}
              onUpdateRule={async (id, rule, instructions) => {
                await updateRule({ id, rule, instructions });
              }}
              onDeleteRule={deleteRule}
            />
          ))}
        </div>
      )}

      {/* Import modal */}
      {showImportModal && <ImportModal onClose={() => setShowImportModal(false)} onImport={handleImport} />}
    </div>
  );
};
